import { useEffect, useRef } from "react";

/**
 * Avisa cuando el final de la lista entra en pantalla, para traer la tanda
 * siguiente antes de que se toque el fondo (`rootMargin`).
 *
 * `recalcular` vuelve a montar el observador —basta pasarle cuántos elementos
 * hay—. Sin eso, una tanda que cabe entera en pantalla deja el final visible sin
 * volver a "entrar", el observador no dispara de nuevo y el scroll se corta.
 *
 * Devuelve el ref para el elemento del final, que conviene tener siempre pintado
 * (con el botón de "cargar más" dentro): así el nodo no cambia de identidad en
 * cada tanda.
 */
export function useFinALaVista<T extends HTMLElement>(alLlegar: () => void, recalcular: unknown) {
  const ref = useRef<T | null>(null);
  // El callback se lee del ref para que el observador no dependa de él: se
  // rearma solo cuando cambia `recalcular`, no en cada render.
  const cb = useRef(alLlegar);
  cb.current = alLlegar;

  useEffect(() => {
    const nodo = ref.current;
    if (!nodo || typeof IntersectionObserver === "undefined") return;
    const observador = new IntersectionObserver((entradas) => entradas.some((e) => e.isIntersecting) && cb.current(), {
      rootMargin: "300px",
    });
    observador.observe(nodo);
    return () => observador.disconnect();
  }, [recalcular]);

  return ref;
}
